import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Alert,
  AlertTitle,
  Dialog,
  Switch,
  FormControlLabel,
  Stack,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as RunIcon,
  Schedule as ScheduleIcon,
  Event as EventIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import apiService from '../services/api';
import { ScheduledAnalysis, ScheduleType, DateRangeType } from '../types/scheduled';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ScheduleDialog from '../components/analysis/ScheduleDialog';
import EmptyState from '../components/common/EmptyState';
import { useParticipantLabels } from '../hooks/useParticipantLabels';
import { formatLocalDate, formatRelativeTimeFromNow, formatUtcInstant, formatWallClockTime } from '../utils/time';

const NO_DATA_ERROR_PATTERNS = [
  'no gps data found',
  'no data found',
  'specified criteria'
];

const isLikelySelectionDataError = (errorMessage?: string): boolean => {
  if (!errorMessage) return false;
  const normalized = errorMessage.toLowerCase();
  return NO_DATA_ERROR_PATTERNS.some(pattern => normalized.includes(pattern));
};

const getScheduleDeviceTypes = (schedule: ScheduledAnalysis): string[] => {
  const config = schedule.analysis_config as ScheduledAnalysis['analysis_config'] & {
    device_types?: string[];
  };
  const parameterDeviceTypes = config.parameters?.device_types;
  const configuredDeviceTypes = config.device_types || parameterDeviceTypes;

  if (Array.isArray(configuredDeviceTypes)) {
    return configuredDeviceTypes
      .filter((deviceType) => deviceType && String(deviceType).toLowerCase() !== 'all')
      .map(String);
  }

  if (config.device_type && String(config.device_type).toLowerCase() !== 'all') {
    return [String(config.device_type)];
  }

  return [];
};

const getFriendlyScheduleError = (schedule: ScheduledAnalysis): string | null => {
  if (!isLikelySelectionDataError(schedule.last_error)) return null;

  const deviceTypes = getScheduleDeviceTypes(schedule);
  const hasSpecificDevices = deviceTypes.length > 0;
  const deviceLabel = deviceTypes.join(', ');

  if (hasSpecificDevices) {
    return `No matching ${deviceLabel} GPS data was found for the selected target and date range. The entity must have that device type assigned and reporting during the selected window for this analysis to run.`;
  }

  return 'No GPS data was found for the selected target and date range. Confirm the selected entities have location data during the scheduled window.';
};

const ScheduledAnalysesPage: React.FC = () => {
  const { plural } = useParticipantLabels();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<ScheduledAnalysis | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  // Fetch scheduled analyses
  const { data: schedules, isLoading, error } = useQuery({
    queryKey: ['scheduledAnalyses'],
    queryFn: () => apiService.getScheduledAnalyses(),
    refetchInterval: 30000  // Refresh every 30 seconds
  });

  // Mutations
  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      apiService.toggleScheduledAnalysis(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledAnalyses'] });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteScheduledAnalysis(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledAnalyses'] });
      setDeleteConfirmId(null);
    }
  });

  const runNowMutation = useMutation({
    mutationFn: (id: number) => apiService.runScheduledAnalysisNow(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['scheduledAnalyses'] });
      alert(`Analysis started! Job ID: ${data.job_id}`);
    }
  });

  const handleCreateNew = () => {
    setEditingSchedule(null);
    setDialogOpen(true);
  };

  const handleEdit = (schedule: ScheduledAnalysis) => {
    setEditingSchedule(schedule);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSchedule(null);
  };

  const handleDialogSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['scheduledAnalyses'] });
    handleCloseDialog();
  };

  const formatSchedule = (schedule: ScheduledAnalysis): string => {
    const scheduleTime = formatWallClockTime(schedule.schedule_time);
    const timezone = schedule.timezone || 'UTC';
    const timeLabel = `${scheduleTime} (${timezone})`;
    
    switch (schedule.schedule_type) {
      case ScheduleType.ONCE:
        return `Once at ${timeLabel}`;
      case ScheduleType.DAILY:
        return `Daily at ${timeLabel}`;
      case ScheduleType.WEEKLY:
        return `Weekly ${schedule.schedule_days} at ${timeLabel}`;
      case ScheduleType.MONTHLY:
        return `Monthly at ${timeLabel}`;
      default:
        return `${schedule.schedule_type} at ${timeLabel}`;
    }
  };

  const formatDateRange = (dateRangeType: DateRangeType): string => {
    return dateRangeType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatNextRun = (nextRun?: string): string => {
    return formatRelativeTimeFromNow(nextRun);
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <Alert severity="error">Failed to load scheduled analyses</Alert>;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ pb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Box sx={{ width: 4, alignSelf: 'stretch', minHeight: 44, borderRadius: 1, bgcolor: 'secondary.main' }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.15, color: 'text.primary' }}>
                Scheduled Analyses
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Automate recurring analysis jobs with customizable schedules
              </Typography>
            </Box>
          </Box>
          {schedules && schedules.length > 0 && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
              size="large"
            >
              Create Schedule
            </Button>
          )}
        </Stack>
      </Box>

      {/* Empty state */}
      {schedules && schedules.length === 0 && (
        <EmptyState
          icon={<ScheduleIcon />}
          title="No scheduled analyses yet"
          description="Automate recurring analysis jobs so they run on your schedule, hands-free."
          action={{ label: 'Create schedule', icon: <AddIcon />, onClick: handleCreateNew }}
          sx={{ mb: 3 }}
        />
      )}

      <Grid container spacing={3}>
        {schedules?.map((schedule) => {
          const friendlyError = getFriendlyScheduleError(schedule);

          return (
          <Grid item xs={12} md={6} lg={4} key={schedule.id}>
            <Card
              sx={{
                height: '100%',
                border: schedule.is_active ? '2px solid' : '1px solid',
                borderColor: schedule.is_active ? 'primary.main' : 'divider',
                backgroundColor: schedule.is_active ? 'background.paper' : 'action.hover'
              }}
            >
              <CardContent>
                {/* Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {schedule.name}
                    </Typography>
                    {schedule.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {schedule.description}
                      </Typography>
                    )}
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={schedule.is_active}
                        onChange={(e) => toggleMutation.mutate({ id: schedule.id, isActive: e.target.checked })}
                        size="small"
                      />
                    }
                    label={<Typography variant="caption">{schedule.is_active ? 'Active' : 'Inactive'}</Typography>}
                    labelPlacement="start"
                    sx={{ m: 0 }}
                  />
                </Stack>

                {/* Configuration Info */}
                <Stack spacing={1.5} sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Schedule
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TimeIcon fontSize="small" color="primary" />
                      <Typography variant="body2">{formatSchedule(schedule)}</Typography>
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Date Range
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <EventIcon fontSize="small" color="primary" />
                      <Typography variant="body2">{formatDateRange(schedule.date_range_type)}</Typography>
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Target
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {schedule.analysis_config.students?.map((student) => (
                        <Chip key={student} label={student} size="small" />
                      )) || <Chip label={`All ${plural}`} size="small" />}
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Analysis Type
                    </Typography>
                    <Chip 
                      label={schedule.analysis_config.analysis_type.replace('_', ' ').toUpperCase()}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </Stack>

                {/* Status */}
                <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, mb: 2 }}>
                  <Stack spacing={0.5}>
                    {schedule.is_active && schedule.next_run_at && (
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Next Run: {formatNextRun(schedule.next_run_at)} ({formatUtcInstant(schedule.next_run_at)})
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Runs: {schedule.run_count} | Created: {formatLocalDate(schedule.created_at)}
                    </Typography>
                    {schedule.last_run_at && (
                      <Typography variant="caption" color="text.secondary">
                        Last Run: {formatUtcInstant(schedule.last_run_at)}
                      </Typography>
                    )}
                    {schedule.last_error && (
                      friendlyError ? (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          <AlertTitle sx={{ fontSize: '0.8rem', mb: 0.25 }}>
                            No data for selection
                          </AlertTitle>
                          <Typography variant="caption" display="block">
                            {friendlyError}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            Details: {schedule.last_error}
                          </Typography>
                        </Alert>
                      ) : (
                        <Typography variant="caption" color="error.main">
                          Error: {schedule.last_error}
                        </Typography>
                      )
                    )}
                  </Stack>
                </Box>

                {/* Actions */}
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Tooltip title="Run Now">
                    <IconButton
                      size="small"
                      onClick={() => runNowMutation.mutate(schedule.id)}
                      disabled={runNowMutation.isPending}
                    >
                      <RunIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(schedule)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => setDeleteConfirmId(schedule.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          );
        })}
      </Grid>

      {/* Create/Edit Dialog */}
      <ScheduleDialog
        open={dialogOpen}
        schedule={editingSchedule}
        onClose={handleCloseDialog}
        onSuccess={handleDialogSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Delete Scheduled Analysis?
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            This action cannot be undone. The schedule will be permanently deleted.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => deleteConfirmId && deleteMutation.mutate(deleteConfirmId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Delete'}
            </Button>
          </Stack>
        </CardContent>
      </Dialog>
    </Box>
  );
};

export default ScheduledAnalysesPage;

